import { enrollTotp, verifyTotp } from "@/features/auth/api/mfa";
import { mfaSearchSchema } from "@/features/auth/search/mfaSearch";
import { useLogoutMutation } from "@/features/auth/api/useLogoutMutation";
import { MfaEnrollScreen } from "@/features/auth/screens/MfaEnrollScreen";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { authQueryOptions } from "@/features/auth/api/useAuthQuery";

type Enrollment = Awaited<ReturnType<typeof enrollTotp>>;

function MfaEnrollPage() {
    const { returnTo } = Route.useSearch();
    const logout = useLogoutMutation();
    const started = useRef(false);
    const [enrollment, setEnrollment] = useState<Enrollment>();
    const [pending, setPending] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (started.current) return;
        started.current = true;
        void enrollTotp()
            .then(setEnrollment)
            .catch((cause: unknown) => {
                setError(cause instanceof Error ? cause.message : "Could not start authenticator setup.");
            });
    }, []);

    const verify = async (code: string) => {
        if (!enrollment) return;
        setPending(true);
        setError("");
        try {
            await verifyTotp(enrollment.id, code);
            window.location.assign(returnTo);
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "The code could not be verified.");
            setPending(false);
        }
    };

    return (
        <MfaEnrollScreen
            enrollment={enrollment ? { qrCode: enrollment.totp.qr_code, secret: enrollment.totp.secret } : undefined}
            pending={pending}
            error={error}
            onVerify={(code) => void verify(code)}
            onLogout={() => logout.mutate()}
        />
    );
}

export const Route = createFileRoute("/_auth/mfa/enroll")({
    component: MfaEnrollPage,
    validateSearch: (search) => mfaSearchSchema.parse(search ?? {}),
    beforeLoad: async ({ context }) => {
        if (!(await context.queryClient.ensureQueryData(authQueryOptions()))) throw redirect({ to: "/login" });
    },
});
